// pages/SAPSproGui.jsx
// Tampilan "SAP GUI" untuk navigator SPRO — meniru layar SPRO asli:
//   Display IMG (pohon Structure)  →  ikon dokumen = Performance Assistant,
//   ikon jam / double-click       →  Display View "<activity>": Overview (isi tabel).
// Semua data dari CACHE (sap_spro_node / sap_spro_objmap / sap_spro_cfg_snap);
// SAP hanya dibaca saat tombol "Read from SAP" diklik.
// Teks UI literal bahasa Inggris (bukan i18n).
import { useState, useEffect, useCallback, useRef } from 'react';

// 2026-10-15: tombol tarik data langsung dari SAP khusus admin (backend juga menolak non-admin).
const isAdminUser = () => { try { return JSON.parse(localStorage.getItem('auth_user') || '{}').role === 'admin'; } catch { return false; } };


const F = '"Segoe UI", Tahoma, Arial, sans-serif';
const C = {
  frame: '#b9cde2', shell: '#eef3f8', title: '#dce7f2', titleTxt: '#1b3a5c',
  tree: '#ffffff', treeAlt: '#f5f8fb', sel: '#fffbc8', selBorder: '#e8d36a',
  hdr: '#e4ebf2', grid: '#c9d6e3', link: '#0a5ca8', pa: '#fffff0', paHead: '#0b3a6e',
};

// ── Ikon kecil (SVG inline, gaya SAP GUI) ─────────────────────────────────────
const IDoc = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" style={{ flex: '0 0 auto' }}>
    <path d="M3 1.5h5.5L11 4v8.5H3z" fill="#fff" stroke="#6b7f95" />
    <path d="M8.5 1.5V4H11" fill="none" stroke="#6b7f95" />
    <path d="M4.5 6.5h5M4.5 8.5h5M4.5 10.5h3.5" stroke="#8aa0b8" />
  </svg>
);
const IExec = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" style={{ flex: '0 0 auto' }}>
    <circle cx="7" cy="7" r="5.6" fill="#fff" stroke="#3f8f3a" strokeWidth="1.3" />
    <path d="M7 3.6V7l2.4 1.4" fill="none" stroke="#3f8f3a" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const IFolder = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" style={{ flex: '0 0 auto' }}>
    <path d="M1.5 3.5h4l1 1.2h6v7.3h-11z" fill={open ? '#fdf1c7' : '#fbe7a6'} stroke="#b8963c" />
  </svg>
);
const Tri = ({ open, show }) => (
  <span style={{ width: 12, display: 'inline-block', textAlign: 'center', fontSize: 9, color: '#344b63', flex: '0 0 auto' }}>
    {show ? (open ? '▼' : '▶') : '•'}
  </span>
);
const TB = ({ title, onClick, children, disabled }) => (
  <button title={title} onClick={onClick} disabled={disabled}
    style={{ width: 24, height: 22, border: '1px solid transparent', background: 'transparent', borderRadius: 3,
      cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1, display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', padding: 0 }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.border = '1px solid #9fb6ce'; }}
    onMouseLeave={e => { e.currentTarget.style.border = '1px solid transparent'; }}>{children}</button>
);
const Dot = ({ c, ch }) => (
  <span style={{ width: 16, height: 16, borderRadius: 8, background: c, color: '#fff', fontSize: 10, fontWeight: 700,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{ch}</span>
);
const Sep = () => <span style={{ width: 1, height: 18, background: '#b5c6d8', margin: '0 5px' }} />;

// ── Kerangka layar SAP ─────────────────────────────────────────────────────────
function Screen({ title, onBack, onExit, cmd, setCmd, onCmd, appBar, status, children }) {
  return (
    <div style={{ fontFamily: F, fontSize: 12.5, color: '#1c2b3a', border: `1px solid ${C.frame}`, borderRadius: 4,
      background: C.shell, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 620 }}>
      {/* menu bar */}
      <div style={{ display: 'flex', gap: 18, padding: '5px 12px', background: '#f7f9fc', borderBottom: '1px solid #dde6ef', fontSize: 12.5 }}>
        {['Implementation Guide', 'Edit', 'Goto', 'Additional Information', 'Utilities', 'System', 'Help'].map(m => (
          <span key={m} style={{ color: '#2a3f55' }}><u>{m[0]}</u>{m.slice(1)}</span>
        ))}
      </div>
      {/* system toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '3px 10px', background: '#f7f9fc', borderBottom: '1px solid #d3dee9' }}>
        <TB title="Enter" onClick={onCmd}><Dot c="#3a9a3a" ch="✓" /></TB>
        <input value={cmd} onChange={e => setCmd(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') onCmd(); }}
          placeholder="search activity / table"
          style={{ width: 170, height: 18, fontSize: 11.5, border: '1px solid #9fb2c6', padding: '0 4px', margin: '0 4px', fontFamily: F }} />
        <Sep />
        <TB title="Back (F3)" onClick={onBack} disabled={!onBack}><Dot c="#3a9a3a" ch="«" /></TB>
        <TB title="Exit" onClick={onExit} disabled={!onExit}><Dot c="#e08a1e" ch="^" /></TB>
        <TB title="Cancel" onClick={onExit} disabled={!onExit}><Dot c="#d2412f" ch="×" /></TB>
      </div>
      {/* title */}
      <div style={{ background: `linear-gradient(${C.title}, #cfdeec)`, padding: '6px 14px', borderBottom: '1px solid #b9cde2' }}>
        <div style={{ fontSize: 17, fontWeight: 700, fontStyle: 'italic', color: C.titleTxt }}>{title}</div>
      </div>
      {/* application toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '4px 12px', background: '#e6eef6',
        borderBottom: '1px solid #c8d7e6', minHeight: 26, fontSize: 12.5 }}>{appBar}</div>
      <div style={{ flex: 1, position: 'relative', padding: 8, overflow: 'hidden' }}>{children}</div>
      {/* status bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 12px', background: '#e6eef6',
        borderTop: '1px solid #c8d7e6', fontSize: 11.5, color: '#2a3f55' }}>
        <span>{status.msg || ''}</span>
        <span style={{ fontWeight: 600, color: '#0b3a6e' }}>SAP</span>
        <span>{status.sys}</span>
      </div>
    </div>
  );
}

// ── Pohon ─────────────────────────────────────────────────────────────────────
function Node({ api, connId, n, depth, touched, sel, onSel, onDoc, onExec }) {
  const [open, setOpen] = useState(depth === 0);
  const [kids, setKids] = useState(null);
  const [busy, setBusy] = useState(false);
  const isAct = n.node_type === 'IMG';
  const hasKids = !isAct && (n.kids || 0) > 0;
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const d = await api(`/api/sap/customizing/spro/tree?connectionId=${connId}&parent=${encodeURIComponent(n.node_id)}&touched=${touched ? 1 : 0}`);
      setKids(d.nodes || []);
    } catch (_) { setKids([]); } finally { setBusy(false); }
  }, [api, connId, n.node_id, touched]);
  useEffect(() => { if (open && hasKids && kids === null) load(); }, [open, hasKids, kids, load]);
  const isSel = sel && sel.node_id === n.node_id;
  return (
    <div>
      <div onClick={() => { onSel(n); if (hasKids) setOpen(o => !o); }}
        onDoubleClick={() => { if (isAct) onExec(n); }}
        style={{ display: 'flex', alignItems: 'center', gap: 4, height: 19, paddingLeft: 6 + depth * 17, cursor: 'default',
          whiteSpace: 'nowrap', background: isSel ? C.sel : 'transparent',
          outline: isSel ? `1px dotted ${C.selBorder}` : 'none' }}>
        <Tri open={open} show={hasKids} />
        {isAct ? (
          <>
            <span title="IMG activity documentation" onClick={e => { e.stopPropagation(); onSel(n); onDoc(n); }} style={{ cursor: 'pointer', display: 'flex' }}><IDoc /></span>
            <span title="Execute (double-click)" onClick={e => { e.stopPropagation(); onSel(n); onExec(n); }} style={{ cursor: 'pointer', display: 'flex' }}><IExec /></span>
          </>
        ) : (
          <span title="Documentation" onClick={e => { e.stopPropagation(); onSel(n); onDoc(n); }} style={{ cursor: 'pointer', display: 'flex' }}>
            {depth === 0 ? <IDoc /> : <IFolder open={open} />}
          </span>
        )}
        <span style={{ marginLeft: 3 }}>{n.text}</span>
        {n.extension && <span style={{ fontSize: 10.5, color: '#8a6d1c', marginLeft: 6 }}>[{n.extension}]</span>}
        {busy && <span style={{ fontSize: 10.5, color: '#7a8ea3', marginLeft: 6 }}>loading…</span>}
      </div>
      {open && (kids || []).map(k => (
        <Node key={k.node_id} api={api} connId={connId} n={k} depth={depth + 1} touched={touched}
          sel={sel} onSel={onSel} onDoc={onDoc} onExec={onExec} />
      ))}
    </div>
  );
}

// ── Performance Assistant (jendela mengambang) ─────────────────────────────────
function Assistant({ n, onClose, onExec }) {
  const [pos, setPos] = useState({ x: 360, y: 30 });
  const drag = useRef(null);
  useEffect(() => {
    const mv = e => { if (drag.current) setPos({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy }); };
    const up = () => { drag.current = null; };
    window.addEventListener('mousemove', mv); window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', mv); window.removeEventListener('mouseup', up); };
  }, []);
  const isAct = n.node_type === 'IMG';
  const path = String(n.path || '').split(/\s*[>›/]\s*/).filter(Boolean);
  const H = ({ children }) => <div style={{ fontWeight: 700, color: '#1c2b3a', margin: '12px 0 4px' }}>{children}</div>;
  return (
    <div style={{ position: 'absolute', left: pos.x, top: pos.y, width: 520, maxWidth: 'calc(100% - 20px)', zIndex: 20,
      border: '1px solid #6f8fb3', boxShadow: '0 6px 20px rgba(20,40,70,.28)', background: C.pa, fontFamily: 'Arial, sans-serif' }}>
      <div onMouseDown={e => { drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y }; }}
        style={{ background: 'linear-gradient(#4f7fb7,#2f5f98)', color: '#fff', padding: '4px 8px', fontSize: 12.5,
          display: 'flex', justifyContent: 'space-between', cursor: 'move', userSelect: 'none' }}>
        <span>Performance Assistant</span>
        <span onClick={onClose} style={{ cursor: 'pointer', fontWeight: 700 }}>✕</span>
      </div>
      <div style={{ padding: '10px 12px', fontSize: 12.5, lineHeight: 1.45, maxHeight: 460, overflow: 'auto' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.paHead, marginBottom: 8 }}>{n.text}</div>
        {path.length > 0 && <div style={{ color: '#44586d', fontSize: 11.5 }}>{path.join(' → ')}</div>}
        {isAct ? (
          <>
            <H>IMG activity</H>
            <div>Activity ID: <code>{n.activity || n.node_id}</code>{n.extension ? <> · Extension <b>{n.extension}</b></> : null}</div>
            <div>Customizing TRs touching this activity: <b>{n.tr_count || 0}</b></div>
            <H>Objects maintained</H>
            {(n.objects || []).length ? (
              <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>{n.objects.map(o => (
                  <tr key={o.objname + (o.tcode || '')}>
                    <td style={{ padding: '1px 10px 1px 0' }}><span style={{ color: C.link, cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => onExec(n, o.objname)}>{o.objname}</span></td>
                    <td style={{ padding: '1px 10px 1px 0', color: '#44586d' }}>{o.descr || ''}</td>
                    <td style={{ padding: '1px 0', fontFamily: 'monospace', fontSize: 11 }}>{o.tcode || ''}</td>
                  </tr>))}</tbody>
              </table>
            ) : <div style={{ color: '#7a8ea3' }}>No object mapping cached.</div>}
            <H>Activities</H>
            <div>Double-click the activity or click the clock icon to display its table content.</div>
          </>
        ) : (
          <>
            <H>Structure node</H>
            <div>{n.obj_count || 0} tables · {n.act_count || 0} activities · {n.tr_count || 0} TR</div>
          </>
        )}
        <div style={{ marginTop: 14, fontSize: 11, color: '#8a7a3a', borderTop: '1px solid #ece6c4', paddingTop: 6 }}>
          SAP IMG documentation text (DOKTL) is not cached yet — this panel shows the cached SPRO metadata.
        </div>
      </div>
    </div>
  );
}

// ── Pilih objek bila activity punya lebih dari satu tabel/view ────────────────
function ChooseObj({ n, onPick, onClose }) {
  return (
    <div style={{ position: 'absolute', left: '50%', top: 60, transform: 'translateX(-50%)', zIndex: 25, width: 440,
      border: '1px solid #6f8fb3', background: '#f4f7fb', boxShadow: '0 6px 20px rgba(20,40,70,.28)' }}>
      <div style={{ background: 'linear-gradient(#4f7fb7,#2f5f98)', color: '#fff', padding: '4px 8px', fontSize: 12.5,
        display: 'flex', justifyContent: 'space-between' }}><span>Choose Activity</span>
        <span onClick={onClose} style={{ cursor: 'pointer', fontWeight: 700 }}>✕</span></div>
      <div style={{ padding: 8, fontSize: 12.5 }}>
        <div style={{ marginBottom: 6 }}>{n.text}</div>
        {n.objects.map(o => (
          <div key={o.objname + (o.tcode || '')} onDoubleClick={() => onPick(o.objname)} onClick={() => onPick(o.objname)}
            style={{ padding: '3px 6px', background: '#fff', border: `1px solid ${C.grid}`, marginBottom: -1, cursor: 'pointer' }}>
            <b style={{ fontFamily: 'monospace' }}>{o.objname}</b> <span style={{ color: '#44586d' }}>{o.descr || ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Display View "...": Overview ──────────────────────────────────────────────
function TableView({ api, connId, obj, setStatus }) {
  const [d, setD] = useState(null);
  const [base, setBase] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [top, setTop] = useState(0);
  const [cur, setCur] = useState(null);
  const [pos, setPos] = useState(null);
  const load = useCallback(async (tab) => {
    setErr('');
    try {
      const r = await api(`/api/sap/customizing/spro/cfg/snap?connectionId=${connId}&objname=${encodeURIComponent(obj)}${tab ? `&tabname=${encodeURIComponent(tab)}` : ''}`);
      setD(r); setBase(r.tabname || ''); setTop(0); setCur(null);
      setStatus(r.cached ? `Snapshot ${r.tabname} · ${new Date(r.fetched_at).toLocaleString('id-ID')}` : `No cached content for ${r.tabname || obj}`);
    } catch (e) { setErr(e.message); }
  }, [api, connId, obj, setStatus]);
  useEffect(() => { load(''); }, [load]);
  const pull = async () => {
    const t = base || obj;
    if (!window.confirm(`Read the content of ${t} from SAP (max 5,000 rows)?`)) return;
    setBusy(true); setErr('');
    try {
      const r = await api('/api/sap/customizing/spro/cfg/fetch', { method: 'POST', body: JSON.stringify({ tabname: t, conns: [connId] }) });
      if (r.kind !== 'table') setErr(`${t} is a ${r.kind}; base tables: ${(r.bases || []).join(', ') || '—'}`);
      await load(base);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  if (!d) return <div style={{ padding: 10 }}>{err || 'Loading…'}</div>;
  const fields = d.fields || [];
  const keys = new Set(d.keys || []);
  const rows = d.rows || [];
  const PAGE = 22;
  const vis = rows.slice(top, top + PAGE);
  const lbl = f => (d.labels && d.labels[f]) || f;
  const doPos = () => {
    const v = String(pos || '').trim().toUpperCase();
    if (!v) { setPos(null); return; }
    const k0 = [...keys][0] || fields[0];
    const i = rows.findIndex(r => String(r[k0] ?? '').toUpperCase() >= v);
    if (i >= 0) { setTop(i); setCur(i); }
    setPos(null);
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, fontSize: 12 }}>
        {(d.bases || []).length > 1 && (
          <span>Table:&nbsp;
            <select value={base} onChange={e => load(e.target.value)} style={{ fontSize: 12, fontFamily: F }}>
              {d.bases.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </span>
        )}
        <span style={{ color: '#44586d' }}>{d.objname !== d.tabname ? `${d.objname} → ` : ''}<b>{d.tabname}</b>{d.descr ? ` · ${d.descr}` : ''}</span>
        {isAdminUser() && (
        <button onClick={pull} disabled={busy} style={{ fontSize: 12, fontFamily: F, padding: '1px 10px', border: '1px solid #8fa7c0',
          background: 'linear-gradient(#fdfdfd,#e3eaf2)', borderRadius: 3, cursor: 'pointer' }}>
          {busy ? 'Reading…' : d.cached ? 'Read fresh from SAP' : 'Read from SAP'}</button>)}
        {d.truncated ? <span style={{ color: '#a15c00' }}>truncated at {rows.length} rows</span> : null}
        {err && <span style={{ color: '#b42318' }}>{err}</span>}
      </div>
      {!d.cached ? (
        <div style={{ padding: 20, color: '#44586d' }}>Content of <b>{d.tabname || obj}</b> is not cached yet — click “Read from SAP”.</div>
      ) : (
        <>
          <div style={{ display: 'inline-block', maxWidth: '100%', border: `1px solid ${C.grid}`, background: '#fff', overflow: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 12.5, fontFamily: F }}>
              <thead><tr>
                <th style={{ width: 18, background: C.hdr, borderRight: `1px solid ${C.grid}`, borderBottom: `1px solid ${C.grid}` }} />
                {fields.map(f => (
                  <th key={f} title={f} style={{ background: C.hdr, fontWeight: 400, textAlign: 'left', padding: '2px 6px',
                    borderRight: `1px solid ${C.grid}`, borderBottom: `1px solid ${C.grid}`, whiteSpace: 'nowrap', minWidth: 70 }}>{lbl(f)}</th>
                ))}
              </tr></thead>
              <tbody>
                {vis.map((r, i) => {
                  const ix = top + i;
                  return (
                    <tr key={ix} onClick={() => setCur(ix)}>
                      <td style={{ background: cur === ix ? '#f5c86a' : C.hdr, borderRight: `1px solid ${C.grid}`, borderBottom: `1px solid ${C.grid}` }} />
                      {fields.map((f, j) => (
                        <td key={f} style={{ padding: '1px 6px', height: 19, whiteSpace: 'nowrap', borderRight: `1px solid ${C.grid}`,
                          borderBottom: `1px solid ${C.grid}`, background: cur === ix && j === 1 ? '#fff3a8' : keys.has(f) ? '#f3f6f9' : '#fff' }}>
                          {String(r[f] ?? '')}</td>
                      ))}
                    </tr>
                  );
                })}
                {Array.from({ length: Math.max(0, 6 - vis.length) }).map((_, i) => (
                  <tr key={'e' + i}><td style={{ background: C.hdr, borderBottom: `1px solid ${C.grid}`, height: 19 }} />
                    {fields.map(f => <td key={f} style={{ borderRight: `1px solid ${C.grid}`, borderBottom: `1px solid ${C.grid}` }} />)}</tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 40, marginTop: 14, paddingLeft: 180 }}>
            {pos === null ? (
              <button onClick={() => setPos('')} style={{ fontSize: 12.5, fontFamily: F, padding: '2px 30px', border: '1px solid #b08a2e',
                background: 'linear-gradient(#fff6d2,#f3dc8c)', borderRadius: 2, cursor: 'pointer' }}>Position...</button>
            ) : (
              <span>
                <input autoFocus value={pos} onChange={e => setPos(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doPos(); if (e.key === 'Escape') setPos(null); }}
                  placeholder={lbl([...keys][0] || fields[0])} style={{ fontSize: 12, width: 140, fontFamily: F }} />
                <button onClick={doPos} style={{ marginLeft: 4, fontSize: 12 }}>✓</button>
              </span>
            )}
            <span>Entry {rows.length ? top + 1 : 0} of {rows.length}</span>
            <span>
              <button onClick={() => setTop(0)} disabled={!top} style={{ fontSize: 11 }}>⏮</button>
              <button onClick={() => setTop(Math.max(0, top - PAGE))} disabled={!top} style={{ fontSize: 11 }}>◀</button>
              <button onClick={() => setTop(Math.min(Math.max(0, rows.length - PAGE), top + PAGE))} disabled={top + PAGE >= rows.length} style={{ fontSize: 11 }}>▶</button>
              <button onClick={() => setTop(Math.max(0, rows.length - PAGE))} disabled={top + PAGE >= rows.length} style={{ fontSize: 11 }}>⏭</button>
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Komponen utama ────────────────────────────────────────────────────────────
export default function SAPSproGui({ api, connId, conns, stamp }) {
  const [roots, setRoots] = useState(null);
  const [touched, setTouched] = useState(true);
  const [sel, setSel] = useState(null);
  const [doc, setDoc] = useState(null);
  const [choose, setChoose] = useState(null);
  const [exec, setExec] = useState(null); // {n, obj}
  const [cmd, setCmd] = useState('');
  const [hits, setHits] = useState(null);
  const [msg, setMsg] = useState('');
  const conn = (conns || []).find(c => c.id === connId);
  const sys = conn ? `${conn.sid} (1) ${conn.client}` : String(connId || '');

  useEffect(() => {
    let dead = false; setRoots(null);
    api(`/api/sap/customizing/spro/tree?connectionId=${connId}&touched=${touched ? 1 : 0}`)
      .then(d => { if (!dead) setRoots(d.nodes || []); }).catch(e => { if (!dead) { setRoots([]); setMsg(e.message); } });
    return () => { dead = true; };
  }, [api, connId, touched, stamp]);

  const onExec = (n, obj) => {
    const objs = n.objects || [];
    if (obj) { setDoc(null); setChoose(null); setExec({ n, obj }); return; }
    if (!objs.length) { setMsg(`No table/view mapped to "${n.text}"`); return; }
    if (objs.length === 1) { setExec({ n, obj: objs[0].objname }); return; }
    setChoose(n);
  };
  const onCmd = () => {
    const q = cmd.trim();
    if (q.length < 2) { setHits(null); return; }
    api(`/api/sap/customizing/spro/search?connectionId=${connId}&q=${encodeURIComponent(q)}`)
      .then(d => setHits((d.rows || []).map(r => ({
        node_type: 'IMG', node_id: r.node_id || r.objname, text: r.act_text || r.objname, path: r.path,
        tr_count: r.tr_count, extension: r.extension, activity: r.activity,
        objects: [{ objname: r.objname, descr: r.descr, tcode: r.tcode }],
      })))).catch(() => setHits([]));
  };

  if (exec) {
    return (
      <Screen title={`Display View "${exec.n.text}": Overview`} cmd={cmd} setCmd={setCmd} onCmd={() => {}}
        onBack={() => { setExec(null); setMsg(''); }} onExit={() => { setExec(null); setMsg(''); }}
        status={{ msg, sys }}
        appBar={<><span style={{ color: '#6b7f95' }}>✎</span><span style={{ color: '#6b7f95' }}>New Entries</span>
          <span style={{ color: '#6b7f95' }}>⧉ ✖ ↶</span><span style={{ fontSize: 11, color: '#6b7f95' }}>read-only (cache)</span></>}>
        <TableView api={api} connId={connId} obj={exec.obj} setStatus={setMsg} />
      </Screen>
    );
  }

  return (
    <Screen title="Display IMG" cmd={cmd} setCmd={setCmd} onCmd={onCmd} status={{ msg, sys }}
      onBack={hits ? () => setHits(null) : null}
      appBar={<>
        <span>Existing BC Sets</span><span style={{ color: '#7a8ea3' }}>BC Sets for Activity</span>
        <span style={{ color: '#7a8ea3' }}>Activated BC Sets for Activity</span>
        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5 }}>
          <input type="checkbox" checked={touched} onChange={e => setTouched(e.target.checked)} />
          Only branches changed by customizing TRs
        </label>
      </>}>
      <div style={{ border: `1px solid ${C.grid}`, background: C.tree, height: 560, display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: C.hdr, borderBottom: `1px solid ${C.grid}`, padding: '2px 8px' }}>Structure</div>
        <div style={{ flex: 1, overflow: 'auto', paddingTop: 2 }}>
          {hits ? (
            hits.length ? hits.map((h, i) => (
              <div key={i} onClick={() => { setSel(h); setDoc(h); }} onDoubleClick={() => onExec(h)}
                style={{ display: 'flex', gap: 5, alignItems: 'center', height: 19, padding: '0 8px', whiteSpace: 'nowrap',
                  background: sel === h ? C.sel : 'transparent' }}>
                <IDoc /><IExec /><span>{h.text || h.act_text || h.objname}</span>
                <span style={{ color: '#7a8ea3', fontSize: 11 }}>{h.path || ''}</span>
              </div>
            )) : <div style={{ padding: 8, color: '#7a8ea3' }}>No hits for “{cmd}”.</div>
          ) : roots === null ? <div style={{ padding: 8 }}>Loading…</div>
            : !roots.length ? <div style={{ padding: 8, color: '#7a8ea3' }}>No SPRO structure cached yet — click “Pull SPRO from SAP”.</div>
            : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, height: 19, paddingLeft: 6 }}>
                  <Tri open show /><IDoc /><span style={{ marginLeft: 3 }}>SAP Customizing Implementation Guide</span>
                </div>
                {roots.map(n => (
                  <Node key={n.node_id} api={api} connId={connId} n={n} depth={1} touched={touched}
                    sel={sel} onSel={setSel} onDoc={setDoc} onExec={onExec} />
                ))}
              </>
            )}
        </div>
      </div>
      {doc && <Assistant n={doc} onClose={() => setDoc(null)} onExec={onExec} />}
      {choose && <ChooseObj n={choose} onClose={() => setChoose(null)} onPick={o => { setChoose(null); setExec({ n: choose, obj: o }); }} />}
    </Screen>
  );
}
